import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import {
  Spin,
  DatePicker,
  Select,
  Button
} from 'antd';
import {
  notificationsAtom,
  payoutsHeaderAtom
} from '../../../../../atoms/Atoms';
import api from '../../../../../api/api';
import { getDatePresets, thousand } from '../../../../../functions';
import Card from '../../../../shared-components/container/Card';
import PayoutsSummaryTable from './PayoutsSummaryTable';

function PayoutsSummary({ orgCode, useGlobal }) {
  const referralCode = orgCode;
  const { RangePicker } = DatePicker;
  const { Option } = Select;
  const history = useHistory();
  const { t } = useTranslation();
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [payoutHeader, setPayoutHeader] = useRecoilState(payoutsHeaderAtom);
  const [selectedTab, setSelectedTab] = useState(1);
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);

  function getOrganizations() {
    setLoadingOrganizations(true);
    api.get('organizations/active')
      .then((response) => {
        setLoadingOrganizations(false);
        if (response?.data?.success) {
          setOrganizations(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoadingOrganizations(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      })
  }

  useEffect(() => {
    setPayoutHeader({
      ...payoutHeader,
      loading: false,
      scheduledInstalls: 0,
      installed: 0,
      scheduledPayments: 0,
      totalLeads: 0,
      leads: [],
      start: dayjs().startOf('week').format('MMM DD, YYYY'),
      end: dayjs().endOf('week').format('MMM DD, YYYY'),
      selectedCloser: '',
      selectedSetter: '',
      selectedOrgId: '',
      selectedSort: 0,
      reload: false
    });
    if (useGlobal) {
      getOrganizations();
    }
  }, []);

  useEffect(() => {
    setPayoutHeader({
      ...payoutHeader,
      loading: true
    });
  }, [selectedTab])

  return (
    <div>
      <div className="flex space-x-2 mb-2">
        {useGlobal && (
          <Select
            showSearch
            style={{ width: 200 }}
            className="ml-auto h-12"
            filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
            value={payoutHeader?.selectedOrgId}
            loading={loadingOrganizations}
            disabled={payoutHeader?.loading}
            onChange={(obj) => setPayoutHeader({
              ...payoutHeader,
              selectedOrgId: obj
            })}
          >
            <Option value="">{t('any_organization')}</Option>
            <Option value="-1">{t('no_organization')}</Option>
            {_.map(organizations, (c) => (
              <Option value={c.id}>{c.nameEn}</Option>
            ))}
          </Select>
        )}
        <Select
          showSearch
          style={{ width: 200 }}
          className="ml-auto h-12"
          filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
          value={payoutHeader?.selectedCloser}
          disabled={payoutHeader?.loading}
          onChange={(obj) => setPayoutHeader({
            ...payoutHeader,
            selectedCloser: obj
          })}
        >
          <Option value="">{t('any_closer')}</Option>
          {_.map(_.orderBy(payoutHeader?.closers, (c1) => c1.completeName), (c) => (
            <Option value={c.id}>{c.completeName}</Option>
          ))}
        </Select>
        <Select
          showSearch
          style={{ width: 200 }}
          className="h-12"
          filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
          value={payoutHeader?.selectedSetter}
          disabled={payoutHeader?.loading}
          onChange={(obj) => setPayoutHeader({
            ...payoutHeader,
            selectedSetter: obj
          })}
        >
          <Option value="">{t('any_setter')}</Option>
          {_.map(_.orderBy(payoutHeader?.setters, (c1) => c1.completeName), (c) => (
            <Option value={c.id}>{c.completeName}</Option>
          ))}
        </Select>
        <RangePicker
          className="bg-transparent h-12"
          presets={getDatePresets(t)}
          disabled={payoutHeader?.loading}
          value={[
            dayjs(payoutHeader?.start),
            dayjs(payoutHeader?.end)
          ]}
          onChange={(obj) => {
            setPayoutHeader({
              ...payoutHeader,
              start: obj[0],
              end: obj[1]
            });
          }}
          format="MMM DD, YYYY"
        />
        <Select
          style={{ width: 200 }}
          className="ml-auto h-12"
          value={payoutHeader?.selectedSort}
          disabled={payoutHeader?.loading}
          onChange={(obj) => setPayoutHeader({
            ...payoutHeader,
            selectedSort: obj
          })}
        >
          <Option value={0}>{t('asc')}</Option>
          <Option value={1}>{t('desc')}</Option>
        </Select>
      </div>
      <div className="grid gap-4 my-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <Spin spinning={payoutHeader?.loading}>
          <button
            aria-label={t('scheduled_installs')}
            type="button"
            className={`col-span-1 w-full border-4 rounded-sm ${selectedTab === 1 ? 'border-primaryColor' : 'border-transparent'}`}
            onClick={() => setSelectedTab(1)}
          >
            <Card
              title={t('scheduled_installs')}
              description={thousand(payoutHeader?.scheduledInstalls)}             
            />
          </button>
        </Spin>
        <Spin spinning={payoutHeader?.loading}>
          <button
            aria-label={t('installed')}
            type="button"
            className={`col-span-1 w-full border-4 rounded-sm ${selectedTab === 2 ? 'border-primaryColor' : 'border-transparent'}`}
            onClick={() => setSelectedTab(2)}
          >
            <Card
              title={t('installed')}
              description={thousand(payoutHeader?.installed)}             
            />
          </button>
        </Spin>
        <Spin spinning={payoutHeader?.loading}>
          <button
            aria-label={t('scheduled_payouts')}
            type="button"
            className={`col-span-1 w-full border-4 rounded-sm ${selectedTab === 3 ? 'border-primaryColor' : 'border-transparent'}`}
            onClick={() => setSelectedTab(3)}
          >
            <Card
              title={t('scheduled_payouts')}
              description={thousand(payoutHeader?.scheduledPayments)}             
            />
          </button>
        </Spin>
        <Spin spinning={payoutHeader?.loading}>
          <button
            aria-label={t('paid')}
            type="button"
            className={`col-span-1 w-full border-4 rounded-sm ${selectedTab === 4 ? 'border-primaryColor' : 'border-transparent'}`}
            onClick={() => setSelectedTab(4)}
          >
            <Card
              title={t('paid')}
              description={thousand(payoutHeader?.paid)}             
            />
          </button>
        </Spin>
      </div>
      <Spin spinning={payoutHeader?.loading}>
        {selectedTab === 1 && (
          <PayoutsSummaryTable className="mt-2" orgCode={referralCode} useGlobal={useGlobal} filter={0} />
        )}
        {selectedTab === 2 && (
          <PayoutsSummaryTable className="mt-2" orgCode={referralCode} useGlobal={useGlobal} filter={1} />
        )}
        {selectedTab === 3 && (
          <PayoutsSummaryTable className="mt-2" orgCode={referralCode} useGlobal={useGlobal} filter={2} />
        )}
        {selectedTab === 4 && (
          <PayoutsSummaryTable className="mt-2" orgCode={referralCode} useGlobal={useGlobal} filter={3} />
        )}
      </Spin>
    </div>
  );
}

export default PayoutsSummary;
